import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Title, Text } from '../../UI/Typography';
import { Button } from '../../UI/Buttons';
import {
  getAllDepartments,
  createDepartment,
  updateDepartment
} from '../../../Services/Api/departmentService';
import DepartmentAddModal from '../Components/DepartmentAddModal';
import DepartmntsTable from '../Components/DepartmntsTable';
import { useAuth } from '../../../Context/AuthContext';
import { usePopup } from '../../../Popups/PopupContext';

const PageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const DrawButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`;

const Table = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary || theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border}10;

  &:last-child {
    border-bottom: none;
  }
`;

const RowTitle = styled(Title).attrs({ size: 'sm' })`
  margin: 0;
  font-weight: 600;
`;

const RowDesc = styled(Text)`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function DepartmentsMain() {
  const { user } = useAuth();
  const { showError, showSuccess } = usePopup();
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);

  const refreshDepartments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!user?.company?.id) throw new Error('No company ID found for current user.');
      const data = await getAllDepartments(user.company.id);
      setDepartments(data || []);
    } catch (err) {
      const msg = err.message || 'Failed to load departments.';
      setError(msg);
      showError('Failed to Load Departments', msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => setIsAddModalOpen(true);
  const closeAddModal = () => setIsAddModalOpen(false);

  const openEditModal = (dept) => {
    setSelectedDept(dept);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setSelectedDept(null);
    setIsEditModalOpen(false);
  };

  const handleSave = async (payload) => {
    try {
      if (selectedDept && selectedDept.dep_id) {
        await updateDepartment(selectedDept.dep_id, payload);
        showSuccess('Department updated');
      } else {
        await createDepartment({ ...payload, companyId: user.company.id });
        showSuccess('Department created');
      }
      await refreshDepartments();
    } catch (err) {
      showError('Save Failed', err.message || 'Unable to save department.');
      throw err;
    }
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Title>Department Management</Title>
        <DrawButton onClick={openAddModal}>New Department</DrawButton>
      </HeaderContainer>

      {isLoading && <Text style={{ padding: '1rem' }}>Loading departments...</Text>}
      {error && <Text style={{ padding: '1rem' }} color="error">Error: {error}</Text>}

      {!isLoading && !error && (
        <DepartmntsTable
          departments={departments}
          onRefresh={refreshDepartments}
          onEdit={openEditModal}
        />
      )}

      <DepartmentAddModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSave={async (data) => {
          await handleSave(data);
          closeAddModal();
        }}
      />

      <DepartmentAddModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        initialData={{
          name: selectedDept?.dep_name || '',
          description: selectedDept?.description || '',
          head: selectedDept?.head?.id || undefined,
        }}
        onSave={async (data) => {
          await handleSave(data);
          closeEditModal();
        }}
      />
    </PageContainer>
  );
}

export default DepartmentsMain;